import { Link } from "react-router-dom";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import {
  User, ArrowDown, ArrowUp, Minus, Share2, Eye, Heart,
  Sun, Moon, Trash2, Check, Flame, Sparkles, Crown, Info
} from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { AiMark } from "./AiMark";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./ui/tooltip";

const DECREASE_METRICS = new Set(["breakouts", "oiliness", "irritation", "dark_spots", "pore_size"]);

const getResultDirection = (metric, value) => {
  const isDecrease = DECREASE_METRICS.has(metric);
  const val = (value || "").toLowerCase();
  const isImproved = ["much_better", "better", "improved", "reduced"].includes(val);
  const isSame = ["same", "no_change"].includes(val);
  if (isSame) return { arrow: Minus, color: "text-amber-500 bg-amber-50" };
  if (isImproved) {
    return isDecrease
      ? { arrow: ArrowDown, color: "text-emerald-600 bg-emerald-50" }
      : { arrow: ArrowUp, color: "text-emerald-600 bg-emerald-50" };
  }
  return isDecrease
    ? { arrow: ArrowUp, color: "text-red-500 bg-red-50" }
    : { arrow: ArrowDown, color: "text-red-500 bg-red-50" };
};

const formatMetric = (m) => m.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());

const SKIN_TYPE_COLORS = {
  "oily": "text-amber-700 bg-amber-50 border-amber-200",
  "dry": "text-sky-700 bg-sky-50 border-sky-200",
  "combination": "text-teal-700 bg-teal-50 border-teal-200",
  "sensitive": "text-pink-700 bg-pink-50 border-pink-200",
  "acne-prone": "text-orange-700 bg-orange-50 border-orange-200",
  "mature": "text-purple-700 bg-purple-50 border-purple-200",
  "normal": "text-emerald-700 bg-emerald-50 border-emerald-200",
};

const getSkinTypeColor = (skinType) => {
  const lower = (skinType || "").toLowerCase();
  for (const [key, val] of Object.entries(SKIN_TYPE_COLORS)) {
    if (lower.includes(key)) return val;
  }
  return "text-[#4A55A6] bg-[#4A55A6]/[0.08] border-[#4A55A6]/20";
};

const extractBestFor = (name) => {
  const lower = name.toLowerCase();
  const types = [];
  if (lower.includes("oily")) types.push("Oily");
  if (lower.includes("dry")) types.push("Dry");
  if (lower.includes("combination")) types.push("Combination");
  if (lower.includes("sensitive")) types.push("Sensitive");
  if (lower.includes("acne")) types.push("Acne-Prone");
  if (lower.includes("aging") || lower.includes("anti-age")) types.push("Mature");
  return types.length > 0 ? types.join(" ") + " Skin" : "All Skin Types";
};

/* Map product category/name to one of 4 consistent action labels */
const getActionLabel = (product) => {
  const cat = (product.category || "").toLowerCase();
  const name = (product.name || "").toLowerCase();

  // Cleanse: cleansers, face washes, exfoliants, toners, masks
  if (cat === "cleanser" || name.includes("cleanser") || name.includes("face wash") || name.includes("cleansing")) return "Cleanse";
  if (cat === "exfoliant" || name.includes("exfoli") || name.includes("bha") || name.includes("aha") || name.includes("peel")) return "Cleanse";
  if (cat === "toner" || name.includes("toner") || name.includes("toning")) return "Cleanse";
  if (name.includes("mask") || name.includes("pack")) return "Cleanse";

  // Protect: sunscreens, SPF
  if (cat === "sunscreen" || name.includes("sunscreen") || name.includes("spf") || name.includes("sun ")) return "Protect";

  // Hydrate: moisturizers, creams, lotions, barrier creams, hyaluronic
  if (cat === "moisturizer" || name.includes("moistur") || name.includes("cream") || name.includes("lotion") || cat === "barrier_cream") return "Hydrate";
  if (name.includes("hyaluronic") || name.includes("hydrat")) return "Hydrate";

  // Treat: serums, actives, spot treatments, everything else
  return "Treat";
};

/* Classify products into AM and PM */
const classifySteps = (products) => {
  const am = [];
  const pm = [];
  for (const p of products) {
    const cat = (p.category || "").toLowerCase();
    const name = (p.name || "").toLowerCase();
    const isSunscreen = cat === "sunscreen" || name.includes("sunscreen") || name.includes("spf");
    const isCleanse = cat === "cleanser" || name.includes("cleanser") || name.includes("face wash");

    if (isSunscreen) {
      am.push(p);
    } else if (isCleanse) {
      am.push(p);
      pm.push(p);
    } else {
      pm.push(p);
    }
  }
  return { am, pm };
};

/**
 * RoutineCard — Option A: Detailed Step Card
 */
export const RoutineCard = ({
  routine,
  compact = false,
  onLike,
  liked = false,
  onDelete,
  detailLink,
  trending = false,
  showLabel = false,
  highlightProductId = null,
  routineOfTheWeekId = null,
}) => {
  const [shareCopied, setShareCopied] = useState(false);
  const [provenanceOpen, setProvenanceOpen] = useState(false);

  if (!routine) return null;

  const outcomes = routine.outcomes || {};
  const products = routine.products || [];
  const bestFor = extractBestFor(routine.name);
  const { am, pm } = classifySteps(products);
  const helpfulCount = routine.likes ?? 0;
  const hasOutcomes = Object.keys(outcomes).length > 0;
  const link = detailLink || `/routines/public/${routine.routine_id}`;
  const initials = (routine.user_name || "U").split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();
  const isRoutineOfTheWeek = routineOfTheWeekId && routine.routine_id === routineOfTheWeekId;
  const isAi = !!routine.is_ai || !!routine.ai_provenance;
  const aiProv = routine.ai_provenance || null;
  const aiAccent = (aiProv && aiProv.accent_gradient) || {};
  const aiGradientCss = `linear-gradient(135deg, ${aiAccent.from || "#4A55A6"}, ${aiAccent.via || "#8B5CF6"}, ${aiAccent.to || "#3d6fbe"})`;

  const cardInner = (
    <Card
      className="bg-white border border-slate-100 rounded-2xl overflow-hidden hover:shadow-lg transition-all relative group"
      data-testid={`routine-card-${routine.routine_id}`}
    >
      {/* AI provenance top strip (Phase 1) */}
      {isAi && (
        <div
          className="absolute top-0 left-0 right-0 h-1 z-[5]"
          style={{ background: aiGradientCss }}
          data-testid={`ai-strip-${routine.routine_id}`}
        />
      )}
      {isRoutineOfTheWeek && (
        <div
          className="absolute top-3 right-3 z-10 px-2.5 py-1 rounded-full text-[10px] font-extrabold uppercase tracking-wider text-white shadow-md flex items-center gap-1"
          style={{ background: 'linear-gradient(135deg, #D4A017 0%, #E6B943 50%, #B87333 100%)' }}
          data-testid="badge-routine-of-the-week"
          title="Most-loved routine this week"
        >
          <Crown className="w-3 h-3" /> Routine of the Week
        </div>
      )}
      {/* Delete button (owner mode) */}
      {onDelete && (
        <button
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); onDelete(routine.routine_id); }}
          className="absolute top-3 right-3 p-1.5 rounded-lg bg-white/80 border border-slate-100 opacity-0 group-hover:opacity-100 hover:bg-red-50 hover:border-red-200 transition-all z-10"
          data-testid={`delete-routine-${routine.routine_id}`}
        >
          <Trash2 className="w-3.5 h-3.5 text-red-400" />
        </button>
      )}

      {/* Trending stripe */}
      {trending && (
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-orange-400 via-amber-400 to-orange-400 rounded-t-2xl" />
      )}

      <CardContent className="p-0">
        {/* Header band */}
        <div className="flex items-center justify-between px-5 py-3 bg-[#4A55A6]/[0.07] border-b border-[#4A55A6]/[0.10]">
          <span className="text-[11px] font-bold uppercase tracking-widest inline-flex items-center gap-1.5" data-testid={`routine-type-label-${routine.routine_id}`}>
            {isAi ? (
              <>
                <AiMark className="w-4 h-4" withSparkle={false} />
                <span className="text-gradient-brand">AI Routine</span>
                <TooltipProvider delayDuration={150}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setProvenanceOpen((v) => !v); }}
                        className="inline-flex items-center justify-center w-3.5 h-3.5 rounded-full bg-[#4A55A6]/20 text-[#4A55A6] hover:bg-[#4A55A6]/30 transition-colors"
                        aria-label="About AI routines"
                        data-testid={`ai-info-btn-${routine.routine_id}`}
                      >
                        <Info className="w-2.5 h-2.5" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" align="start" className="max-w-[260px] text-[11px] leading-relaxed normal-case tracking-normal font-medium">
                      Researched &amp; sourced by Zeel AI — validated against published dermatology studies and community confidence scores. Reviewed by Zeel editors before publishing.
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </>
            ) : (
              <>
                <Sparkles className="w-3.5 h-3.5 text-[#4A55A6]" />
                <span className="text-gradient-brand">Shared Routine</span>
              </>
            )}
          </span>
          <div className="flex items-center gap-2">
            {trending && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-orange-50 border border-orange-200/60 text-orange-600">
                <Flame className="w-3 h-3" />
                <span className="text-[10px] font-bold uppercase tracking-wider">Trending</span>
              </span>
            )}
            <span className={`text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full border ${getSkinTypeColor(bestFor)}`}>
              {bestFor}
            </span>
          </div>
        </div>

        <div className="p-5">
          {/* 1. Header — prominent user */}
          <div className="flex items-start gap-3.5 mb-5">
            <div className="relative shrink-0">
              {routine.user_picture ? (
                <img src={routine.user_picture} alt="" className="w-12 h-12 rounded-full object-cover border-2 border-[#5B6BA8]/20 shadow-sm" />
              ) : (
                <div className="w-12 h-12 rounded-full bg-[#5B6BA8]/10 flex items-center justify-center border-2 border-[#5B6BA8]/15">
                  <span className="text-base font-bold text-[#5B6BA8]">{initials}</span>
                </div>
              )}
              {isAi && (
                <span
                  className="absolute -bottom-1 -right-1 w-[22px] h-[22px] rounded-full bg-white ring-2 ring-white flex items-center justify-center shadow-md"
                  data-testid={`ai-avatar-badge-${routine.routine_id}`}
                  aria-label="AI reviewer"
                >
                  <AiMark className="w-4 h-4" withSparkle={false} />
                </span>
              )}
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="text-[15px] font-bold text-slate-600 leading-snug line-clamp-2">{routine.name}</h3>
              <p className="text-[13px] text-muted-foreground mt-0.5">
                By <span className="font-semibold text-foreground/80">{routine.user_name}</span>
              </p>
              <span className={`inline-block text-[11px] font-semibold mt-1 ${getSkinTypeColor(bestFor).split(" ")[0]}`}>{bestFor}</span>
              {helpfulCount > 0 && (
                <div className="flex items-center gap-1 text-[11px] text-muted-foreground mt-1.5">
                  <Heart className="w-3 h-3 text-rose-400 shrink-0" />
                  {helpfulCount} found this helpful
                </div>
              )}
            </div>
            {isAi ? (
              // AI routines don't carry a real-user weeks-tracked signal. Keep
              // the grid slot reserved so the card doesn't reflow, but render
              // nothing inside.
              <div className="shrink-0 pl-2 w-[44px]" aria-hidden="true" data-testid={`weeks-slot-empty-${routine.routine_id}`} />
            ) : (
              <div className="text-center shrink-0 pl-2">
                <span className="text-lg font-extrabold text-[#3B5998] leading-none">{routine.duration_weeks}</span>
                <p className="text-[9px] text-muted-foreground mt-0.5 leading-tight">weeks</p>
              </div>
            )}
          </div>

          {/* 2. Products Used */}
          <div className="mb-5">
            <div className="flex items-center gap-1.5 mb-2.5">
              <span className="w-[7px] h-[7px] rounded-full bg-[#4A55A6] inline-block" />
              <span className="text-[11px] font-bold uppercase tracking-wider text-[#4A55A6]">Products Used</span>
            </div>
            <div className="flex gap-2.5 overflow-x-auto pb-1 scrollbar-hide">
              {products.slice(0, 8).map((p) => {
                const isHighlighted = highlightProductId && p.product_id === highlightProductId;
                return (
                  <Link
                    key={p.product_id}
                    to={`/product/${p.product_id}`}
                    className="shrink-0 group/prod flex flex-col items-center w-[72px] rounded-lg p-1 transition-all duration-200 hover:-translate-y-1 hover:bg-[#4A55A6]/[0.06] hover:shadow-md"
                    style={isHighlighted ? { background: 'linear-gradient(160deg, rgba(74,85,166,0.18) 0%, rgba(123,91,175,0.20) 50%, rgba(199,107,159,0.14) 100%)' } : {}}
                    title={p.name}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className={`w-14 h-14 rounded-lg overflow-hidden transition-all duration-200 ${
                      isHighlighted ? '' : 'border border-slate-100 bg-slate-50 group-hover/prod:border-[#4A55A6]/60 group-hover/prod:ring-2 group-hover/prod:ring-[#4A55A6]/15'
                    }`}>
                      <img src={p.image_url} alt={p.name} className="w-full h-full object-cover" />
                    </div>
                    <span className="text-[9px] font-bold uppercase tracking-wide text-slate-500 mt-1.5 text-center leading-tight line-clamp-1 w-full group-hover/prod:text-[#4A55A6] transition-colors duration-200">
                      {p.brand || p.name.split(" ")[0]}
                    </span>
                    <span className={`text-[10px] text-center leading-tight line-clamp-2 w-full ${
                      isHighlighted ? 'font-bold text-[#5B6BA8]' : 'text-muted-foreground'
                    }`}>
                      {p.name.replace(p.brand || "", "").trim() || p.name}
                    </span>
                  </Link>
                );
              })}
            </div>
          </div>

          {/* 3. Routine Steps — full numbered list */}
          {!compact && (pm.length > 0 || am.length > 0) && (
            <div className="mb-5">
              <div className="flex items-center gap-1.5 mb-2.5">
                <span className="w-[7px] h-[7px] rounded-full bg-[#4A55A6] inline-block" />
                <span className="text-[11px] font-bold uppercase tracking-wider text-[#4A55A6]">Routine Steps</span>
              </div>
              <div className="bg-slate-50/80 border border-slate-100 rounded-xl overflow-hidden">
                {/* AM Section */}
                {am.length > 0 && (
                  <>
                    <div className="flex items-center gap-1.5 px-3.5 py-2 bg-amber-50/60 border-b border-slate-100">
                      <Sun className="w-3.5 h-3.5 text-amber-500" />
                      <span className="text-[11px] font-bold uppercase tracking-wider text-amber-700">AM · Morning</span>
                    </div>
                    {am.map((p, i) => (
                      <div key={`am-${p.product_id}`} className={`flex items-center px-3.5 py-2.5 ${i < am.length - 1 || pm.length > 0 ? 'border-b border-slate-100' : ''}`}>
                        <div className="w-6 h-6 rounded-full bg-[#3B5998]/10 flex items-center justify-center shrink-0 mr-3">
                          <span className="text-[11px] font-bold text-[#3B5998]">{i + 1}</span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-[9px] font-bold uppercase tracking-wide text-muted-foreground leading-none">{p.brand || p.name.split(" ")[0]}</p>
                          <p className="text-[13px] font-semibold text-slate-600 leading-snug mt-0.5 line-clamp-1">{p.name}</p>
                        </div>
                        <span className="text-[11px] font-semibold text-gradient-brand px-2.5 py-1 rounded-md bg-slate-50 shrink-0 ml-2" style={{ display: 'inline-block' }}>{getActionLabel(p)}</span>
                      </div>
                    ))}
                  </>
                )}
                {/* PM Section */}
                {pm.length > 0 && (
                  <>
                    <div className="flex items-center gap-1.5 px-3.5 py-2 bg-[#4A55A6]/[0.06] border-b border-slate-100">
                      <Moon className="w-3.5 h-3.5 text-indigo-400" />
                      <span className="text-[11px] font-bold uppercase tracking-wider text-[#4A55A6]">PM · Evening</span>
                    </div>
                    {pm.map((p, i) => (
                      <div key={`pm-${p.product_id}`} className={`flex items-center px-3.5 py-2.5 ${i < pm.length - 1 ? 'border-b border-slate-100' : ''}`}>
                        <div className="w-6 h-6 rounded-full bg-[#3B5998]/10 flex items-center justify-center shrink-0 mr-3">
                          <span className="text-[11px] font-bold text-[#3B5998]">{(am.length > 0 ? am.length : 0) + i + 1}</span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-[9px] font-bold uppercase tracking-wide text-muted-foreground leading-none">{p.brand || p.name.split(" ")[0]}</p>
                          <p className="text-[13px] font-semibold text-slate-600 leading-snug mt-0.5 line-clamp-1">{p.name}</p>
                        </div>
                        <span className="text-[11px] font-semibold text-gradient-brand px-2.5 py-1 rounded-md bg-slate-50 shrink-0 ml-2" style={{ display: 'inline-block' }}>{getActionLabel(p)}</span>
                      </div>
                    ))}
                  </>
                )}
              </div>
            </div>
          )}

          {/* 4. Results Reported */}
          {hasOutcomes && (
            <div className="mb-5">
              <div className="flex items-center gap-1.5 mb-2.5">
                <span className="w-[7px] h-[7px] rounded-full bg-[#4A55A6] inline-block" />
                <span className="text-[11px] font-bold uppercase tracking-wider text-[#4A55A6]">Results Reported</span>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {Object.entries(outcomes).map(([metric, value]) => {
                  const result = getResultDirection(metric, value);
                  const Icon = result.arrow;
                  return (
                    <span key={metric} className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold ${result.color}`}>
                      <Icon className="w-3.5 h-3.5 shrink-0" />
                      {formatMetric(metric)}
                    </span>
                  );
                })}
              </div>
            </div>
          )}

          {/* AI Provenance Drawer — sources + generation meta (Phase 1) */}
          {isAi && provenanceOpen && aiProv && (
            <div
              className="mt-4 rounded-xl border p-4 text-xs space-y-2"
              style={{ borderColor: `${aiAccent.from || "#4A55A6"}40`, background: `${aiAccent.from || "#4A55A6"}08` }}
              data-testid={`ai-provenance-drawer-${routine.routine_id}`}
            >
              <p className="font-semibold text-slate-700 leading-snug">
                This is an AI-authored routine, reviewed by an editor before publishing. It does not replace dermatologist advice.
              </p>
              {aiProv.explainer && (
                <p className="text-slate-600 leading-relaxed">{aiProv.explainer}</p>
              )}
              {Array.isArray(aiProv.sources) && aiProv.sources.length > 0 && (
                <div>
                  <p className="font-bold uppercase tracking-wider text-[10px] text-slate-500 mt-2 mb-1">Sources</p>
                  <ul className="space-y-1">
                    {aiProv.sources.map((s, i) => (
                      <li key={i} className="flex items-start gap-2 text-slate-600">
                        <span className="inline-block w-1 h-1 rounded-full mt-1.5 shrink-0" style={{ background: aiAccent.from || "#4A55A6" }} />
                        <span>{s}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {aiProv.model && (
                <p className="text-[10px] text-slate-400 pt-1 border-t border-slate-100">
                  Generated by {aiProv.model}{aiProv.generated_at ? ` · ${new Date(aiProv.generated_at).toLocaleDateString()}` : ""}
                </p>
              )}
            </div>
          )}

          {/* 5. Footer */}
          <div className="border-t border-slate-100 pt-4 flex items-center gap-2">
            {/* Like button (gallery mode) */}
            {onLike && (
              <button
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); onLike(routine.routine_id); }}
                className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium transition-colors border ${
                  liked ? "text-rose-500 bg-rose-50 border-rose-200" : "text-muted-foreground border-slate-200 hover:text-rose-500 hover:border-rose-200"
                }`}
                data-testid={`like-${routine.routine_id}`}
              >
                <Heart className={`w-3.5 h-3.5 ${liked ? "fill-current" : ""}`} />
                {helpfulCount > 0 ? helpfulCount : "Helpful"}
              </button>
            )}

            <Link to={link} className="flex-1" onClick={(e) => e.stopPropagation()}>
              <Button variant="secondary" size="sm" className="w-full rounded-full text-xs gap-1.5 bg-[#3B5998] text-white hover:bg-[#2d4373]" data-testid={`view-routine-${routine.routine_id}`}>
                <Eye className="w-3.5 h-3.5" /> View Routine
              </Button>
            </Link>
            <Button
              variant="outline"
              size="sm"
              className="rounded-full text-xs gap-1.5 px-3"
              data-testid={`share-routine-btn-${routine.routine_id}`}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                const shareUrl = `${window.location.origin}/routines/public/${routine.routine_id}`;
                navigator.clipboard.writeText(shareUrl).then(() => {
                  setShareCopied(true);
                  toast.success("Link copied to clipboard!");
                  setTimeout(() => setShareCopied(false), 2000);
                }).catch(() => {
                  toast.error("Failed to copy link");
                });
              }}
            >
              {shareCopied ? <Check className="w-3.5 h-3.5" /> : <Share2 className="w-3.5 h-3.5" />}
              {shareCopied ? "Copied!" : "Share"}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  // SEO Layer 2 — wrap AI-authored cards in data-nosnippet so Google cannot quote them
  if (isAi) {
    return (
      <div data-nosnippet data-testid={`ai-nosnippet-${routine.routine_id}`}>
        {cardInner}
      </div>
    );
  }
  return cardInner;
};
